import React, { useState } from "react";
import { useDeletePayable, useMarkPayablePaid, usePayables } from "../../api/payables";
import { StatusTag } from "../../components/StatusTag";
import { PaginationBar } from "../../components/PaginationBar";
import { BaixaModal } from "../../components/BaixaModal";
import { DeleteIconButton } from "../../components/RowIconButton";
import { formatBRL, formatDate } from "../../utils/money";
import { pillBtnStyle } from "../../utils/pillButton";
import { NewPayableModal } from "./NewPayableModal";
import { useToast } from "../../components/ToastProvider";

export default function PayablesPage() {
  const [page, setPage] = useState(1);
  const [showNew, setShowNew] = useState(false);
  const [baixaTarget, setBaixaTarget] = useState<{ id: number; name: string } | null>(null);
  const { data, isLoading, isError } = usePayables({ page, pageSize: 7 });
  const markPaid = useMarkPayablePaid();
  const deletePayable = useDeletePayable();
  const toast = useToast();

  async function handleMarkPaid(input: { settlementTypeId: number; paidAt: string }) {
    if (!baixaTarget) return;
    try {
      await markPaid.mutateAsync({ id: baixaTarget.id, ...input });
      toast(`${baixaTarget.name} · pagamento confirmado.`);
      setBaixaTarget(null);
    } catch {
      toast("Não foi possível confirmar o pagamento.");
    }
  }

  async function handleDelete(id: number, name: string) {
    if (!window.confirm(`Excluir a conta "${name}"? Essa ação não pode ser desfeita.`)) return;
    await deletePayable.mutateAsync(id);
    toast(`${name} · conta excluída.`);
  }

  return (
    <div className="d-flex flex-column gap-3">
      <div className="row g-2">
        <div className="col-md-9" />
        <div className="col-md-3">
          <button type="button" className="btn btn-primary w-100" onClick={() => setShowNew(true)}>
            Nova conta a pagar
          </button>
        </div>
      </div>

      {isLoading && <div className="text-secondary">Carregando…</div>}
      {isError && <div className="text-danger">Não foi possível carregar as contas a pagar.</div>}

      {data && (
        <div className="nt-table-wrap" style={{ overflowX: "auto" }}>
          <table className="table nt-table-fade table-striped align-middle">
            <thead>
              <tr>
                <th>Fornecedor</th>
                <th style={{ width: 160 }}>Categoria</th>
                <th style={{ width: 120 }}>Vencimento</th>
                <th className="text-end" style={{ width: 120 }}>Valor</th>
                <th style={{ width: 110 }}>Status</th>
                <th style={{ width: 200 }}></th>
              </tr>
            </thead>
            <tbody>
              {data.data.map((p) => (
                <tr key={p.id}>
                  <td style={{ fontFamily: "var(--font-heading)" }}>{p.supplierName}</td>
                  <td>{p.category}</td>
                  <td className="nt-tabular">{formatDate(p.dueDate)}</td>
                  <td className="text-end nt-tabular">{formatBRL(p.value)}</td>
                  <td>
                    <StatusTag status={p.status} />
                  </td>
                  <td className="text-end">
                    <div className="d-flex gap-2 justify-content-end flex-wrap">
                      {p.status !== "PAGO" && (
                        <button
                          type="button"
                          style={pillBtnStyle("#000000")}
                          onClick={() => setBaixaTarget({ id: p.id, name: p.supplierName })}
                        >
                          Marcar pago
                        </button>
                      )}
                      <DeleteIconButton onClick={() => handleDelete(p.id, p.supplierName)} disabled={deletePayable.isPending} />
                    </div>
                  </td>
                </tr>
              ))}
              {data.data.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-secondary text-center py-4">
                    Nenhuma conta a pagar cadastrada.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {data && <PaginationBar page={data.page} pageSize={data.pageSize} total={data.total} onPageChange={setPage} />}

      <NewPayableModal show={showNew} onClose={() => setShowNew(false)} />
      <BaixaModal
        show={!!baixaTarget}
        title={`Marcar pago · ${baixaTarget?.name ?? ""}`}
        confirmLabel="Marcar pago"
        onClose={() => setBaixaTarget(null)}
        onConfirm={handleMarkPaid}
        loading={markPaid.isPending}
      />
    </div>
  );
}
